import * as Sentry from '@sentry/vue'
import type { UserProfile } from '@/api/generated-users'
import { getFullName } from '@/utils/userFormatting'

/**
 * Sentry tag naming the practice an event came from.
 *
 * A tag rather than only user context: tags are what the Sentry UI can filter on,
 * across errors, feedback and replays alike.
 */
const ORGANIZATION_TAG = 'organization'

/**
 * Attach the signed-in account to every Sentry event — errors, feedback and replays.
 *
 * Without this the only identity on an event is what the reporter typed into the
 * feedback widget, which is self-reported and was wrong on both of the reports that
 * prompted this: one carried a personal Gmail address, the other the address of the
 * colleague the reporter filed on behalf of (#3598). Answering "which practice is
 * this?" then means guessing from the email domain, which a personal address defeats
 * outright.
 *
 * The organisation *name* is business data, not PHI. The user id and email are a
 * deliberate, small widening of what reaches Sentry — the feedback widget already
 * transmitted an address, error events carried none. Replay masking is unaffected.
 */
export function setSentryUserContext(profile: UserProfile): void {
  Sentry.setUser({
    id: profile.id,
    email: profile.email,
    username: getFullName(profile),
  })
  // `undefined` removes the tag rather than writing an empty one, so a user without an
  // organization cannot inherit the previous account's practice on a shared machine.
  Sentry.setTag(ORGANIZATION_TAG, profile.organizationName ?? undefined)
}

/**
 * Drop the account from Sentry's scope, so events raised after sign-out are not
 * attributed to whoever was signed in before.
 */
export function clearSentryUserContext(): void {
  Sentry.setUser(null)
  Sentry.setTag(ORGANIZATION_TAG, undefined)
}
